import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { ingestorsApi } from './ingestors/api';
import type { IngestorInstance, IngestorType } from './ingestors/types';
import { relativeTime, pollerHealthClass } from '../../lib/relativeTime';

interface Team {
  id: string;
  name: string;
  slug: string;
}

export function IngestorsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const instances = useQuery({
    queryKey: ['ingestor-instances'],
    queryFn: () => ingestorsApi.listInstances(),
  });
  const types = useQuery({
    queryKey: ['ingestor-types'],
    queryFn: () => ingestorsApi.listTypes(),
  });
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Team[] }>('/api/teams'),
  });

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      ingestorsApi.updateInstance(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingestor-instances'] }),
  });

  const test = useMutation({
    mutationFn: (id: string) => ingestorsApi.testConnection(id),
  });

  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  const teamName = (id: string) => (teams.data?.teams ?? []).find((t) => t.id === id)?.name ?? id;
  const typeName = (t: string) => (types.data?.types ?? []).find((x) => x.type === t)?.display_name ?? t;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Ingestors</h1>
          <p className="text-ink-muted">
            Configured instances of pluggable data sources. Each instance attributes events to a single team.
          </p>
        </div>
        <Link to="/admin/ingestors/new" className="btn-primary">+ Add ingestor</Link>
      </header>

      {feedback ? (
        <div
          className={feedback.kind === 'ok' ? 'card border-l-4 border-l-accent-success' : 'card border-l-4 border-l-accent-danger'}
          role="status"
        >
          {feedback.msg}
          <button className="btn ml-3" onClick={() => setFeedback(null)}>Dismiss</button>
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr>
              <th className="py-2">Name</th>
              <th>Type</th>
              <th>Last run</th>
              <th>Team</th>
              <th>Enabled</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(instances.data?.instances ?? []).map((i: IngestorInstance) => {
              // M5.8+: a 'Last run' column that's only meaningful
              // for polling ingestors. Webhook ingestors run on
              // incoming requests; they have no scheduled cycle
              // to show a "last run" for. Look up the type's mode
              // to decide which label to render.
              const t = (types.data?.types ?? []).find((x) => x.type === i.type);
              const mode = t?.mode ?? 'webhook';
              // pollIntervalMinutes is on the config; the ingestor
              // types guarantee it for polling/'both' modes.
              // Default to 10 (the schema default in the api) if
              // missing — defensive against legacy configs.
              const intervalMin = Number(
                (i.config as Record<string, unknown>)?.pollIntervalMinutes ?? 10,
              );
              const lastPollAt = i.poller_state?.last_poll_at ?? null;
              const lastError = i.poller_state?.last_error ?? null;
              const health = mode === 'webhook'
                ? null
                : pollerHealthClass(lastPollAt, lastError, intervalMin);
              return (
                <tr key={i.id} className="border-t border-white/5">
                  <td className="py-2">
                    <Link to={`/admin/ingestors/${i.id}`} className="font-medium hover:underline">
                      {i.name}
                    </Link>
                  </td>
                  <td className="font-mono text-xs text-ink-muted">{typeName(i.type)}</td>
                  <td className="text-xs">
                    {mode === 'webhook' ? (
                      <span className="text-ink-muted" title="This ingestor runs on incoming webhooks — no scheduled cycle.">
                        On demand
                      </span>
                    ) : health ? (
                      <span
                        className={`inline-flex items-center gap-1.5 ${health.text}`}
                        title={
                          lastError
                            ? `Last error: ${lastError}`
                            : `Last successful poll: ${lastPollAt ?? 'never'}${lastPollAt ? ` (${intervalMin} min interval)` : ''}`
                        }
                      >
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${health.dot}`}
                          aria-label={health.label}
                        />
                        <span>{relativeTime(lastPollAt)}</span>
                        {lastError ? (
                          <span className="ml-1 text-err/80" aria-label="error">!</span>
                        ) : null}
                      </span>
                    ) : null}
                  </td>
                  <td className="text-ink-muted">{teamName(i.team_id)}</td>
                  <td>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={i.enabled}
                        onChange={(e) =>
                          toggleEnabled.mutate({ id: i.id, enabled: e.target.checked })
                        }
                      />
                      <span className="text-xs text-ink-muted">{i.enabled ? 'on' : 'off'}</span>
                    </label>
                  </td>
                  <td className="text-ink-muted">{new Date(i.created_at).toLocaleDateString()}</td>
                  <td className="space-x-2 text-right">
                    <button
                      className="btn"
                      onClick={async () => {
                        setTesting(i.id);
                        setFeedback(null);
                        try {
                          const r = await test.mutateAsync(i.id);
                          setFeedback({
                            kind: r.ok ? 'ok' : 'err',
                            msg: r.ok
                              ? `Test OK.${r.note ? ` (${r.note})` : ''}`
                              : `Test failed: ${r.error ?? 'unknown error'}`,
                          });
                        } catch (e) {
                          setFeedback({ kind: 'err', msg: e instanceof ApiError ? e.message : 'Test failed.' });
                        } finally {
                          setTesting(null);
                        }
                      }}
                      disabled={testing === i.id}
                    >
                      {testing === i.id ? 'Testing…' : 'Test'}
                    </button>
                    <button className="btn" onClick={() => navigate(`/admin/ingestors/${i.id}`)}>Edit</button>
                  </td>
                </tr>
              );
            })}
            {!(instances.data?.instances ?? []).length ? (
              <tr>
                <td colSpan={7} className="py-6 text-center text-ink-muted">
                  No ingestors yet. <Link to="/admin/ingestors/new" className="underline">Add one</Link> to start scoring events.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {types.data ? (
        <div className="card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Available types</h2>
          <ul className="mt-2 space-y-2">
            {(types.data?.types ?? []).map((t: IngestorType) => (
              <li key={t.type} className="flex items-baseline justify-between">
                <div>
                  <p className="font-medium">{t.display_name} <span className="font-mono text-xs text-ink-dim">({t.type})</span></p>
                  <p className="text-xs text-ink-muted">{t.description}</p>
                </div>
                <p className="text-xs text-ink-dim">
                  {t.secret_fields.length > 0
                    ? `Secrets: ${t.secret_fields.join(', ')}`
                    : 'No secrets'}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
